import { useSearchParams } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Undo2 } from 'lucide-react';
import { getPartyColor, getPartyTextClass, formatCurrency } from '../../utils/partyUtils';

function SortIcon({ column, sortBy, sortOrder }) {
  if (sortBy !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
  return sortOrder === "asc" ? <ArrowUp className="w-3 h-3 ml-1 text-accent" /> : <ArrowDown className="w-3 h-3 ml-1 text-accent" />;
}

export default function DonationsTable({
  donations, loading, pagination, setPagination,
  sortBy, sortOrder, handleSort,
}) {
  const [, setSearchParams] = useSearchParams();

  return (
    <>
      {/* Results Info */}
      <div className="flex items-center justify-between text-sm gap-2 flex-wrap">
        <p className="text-muted-foreground text-xs md:text-sm">
          Showing{" "}
          <span className="font-mono font-medium text-foreground">{((pagination.page - 1) * pagination.pageSize + 1).toLocaleString()}</span>
          -
          <span className="font-mono font-medium text-foreground">{Math.min(pagination.page * pagination.pageSize, pagination.total).toLocaleString()}</span>
          {" "}of{" "}
          <span className="font-mono font-medium text-foreground">{pagination.total.toLocaleString()}</span> records
        </p>
        <Select
          value={pagination.pageSize.toString()}
          onValueChange={(v) => setPagination(prev => ({ ...prev, pageSize: parseInt(v), page: 1 }))}
        >
          <SelectTrigger className="w-32 rounded-sm" data-testid="page-size-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 per page</SelectItem>
            <SelectItem value="50">50 per page</SelectItem>
            <SelectItem value="100">100 per page</SelectItem>
            <SelectItem value="200">200 per page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <Card className="border rounded-sm shadow-sm overflow-hidden" data-testid="donations-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                {[
                  { key: 'report_year', label: 'Year' },
                  { key: 'contributor_name', label: 'Donor' },
                  { key: 'committee_name', label: 'Recipient' },
                  { key: 'contribution_amount', label: 'Amount', right: true },
                  { key: 'contribution_date', label: 'Date' },
                ].map(col => (
                  <TableHead
                    key={col.key}
                    className={`cursor-pointer select-none text-xs uppercase tracking-wider font-medium ${col.right ? 'text-right' : ''}`}
                    onClick={() => handleSort(col.key)}
                    data-testid={`sort-${col.label.toLowerCase()}`}
                  >
                    <div className={`flex items-center ${col.right ? 'justify-end' : ''}`}>
                      {col.label}
                      <SortIcon column={col.key} sortBy={sortBy} sortOrder={sortOrder} />
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-wider font-medium">State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-muted animate-pulse rounded-sm" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : donations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <p className="text-muted-foreground">No donations found</p>
                    <p className="text-sm text-muted-foreground mt-1">Upload a dataset or adjust your filters</p>
                  </TableCell>
                </TableRow>
              ) : (
                donations.map((donation, index) => {
                  const recipientPartyClass = getPartyTextClass(donation.committee_name);
                  const party = getPartyColor(donation.committee_name);
                  const isRefund = donation.contribution_amount < 0;
                  // Refunds get a distinct amber tint that trumps party
                  // colouring so they always visually pop as anomalous rows.
                  const rowBgClass = isRefund
                    ? 'bg-amber-50/60 hover:bg-amber-100/60'
                    : party === 'democrat'
                      ? 'bg-blue-50/30 hover:bg-blue-100/30'
                      : party === 'republican'
                        ? 'bg-red-50/30 hover:bg-red-100/30'
                        : 'hover:bg-muted/30';
                  const amountClass = isRefund
                    ? 'text-amber-700 font-semibold'
                    : recipientPartyClass ? recipientPartyClass + ' font-medium'
                    : donation.contribution_amount > 5000 ? "text-red-600 font-medium"
                    : donation.contribution_amount > 1000 ? "text-amber-600"
                    : "text-emerald-600";
                  return (
                    <TableRow key={donation.id} className={`animate-fade-in stagger-${Math.min(index + 1, 10)} transition-colors duration-75 ${rowBgClass}`} data-testid={`donation-row-${index}`}>
                      <TableCell className="font-mono text-sm">{donation.report_year}</TableCell>
                      <TableCell className="max-w-[200px]">
                        <button
                          onClick={() => setSearchParams({ donor: donation.contributor_name })}
                          className="text-sm truncate block text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left max-w-full"
                          title={donation.contributor_name}
                          data-testid="table-donor-link"
                        >
                          {donation.contributor_name}
                        </button>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <button
                          onClick={() => setSearchParams({ recipient: donation.committee_name })}
                          className={`text-sm truncate block font-medium hover:underline cursor-pointer text-left max-w-full ${recipientPartyClass}`}
                          title={donation.committee_name}
                          data-testid="table-recipient-link"
                        >
                          {donation.committee_name}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isRefund && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
                              title="FEC refund / negative contribution — reattribution, redesignation, or committee refund"
                              data-testid={`refund-badge-${index}`}
                            >
                              <Undo2 className="w-2.5 h-2.5" />
                              Refund
                            </span>
                          )}
                          <span className={`font-mono text-sm ${amountClass}`} data-testid={`donation-amount-${index}`}>
                            {formatCurrency(donation.contribution_amount)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{donation.contribution_date}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{donation.contributor_state || "-"}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <Button variant="outline" size="sm" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1} className="rounded-sm" data-testid="prev-page-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Page</span>
            <span className="font-mono text-sm font-medium">{pagination.page}</span>
            <span className="text-sm text-muted-foreground">of</span>
            <span className="font-mono text-sm font-medium">{pagination.totalPages}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages} className="rounded-sm" data-testid="next-page-btn">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </>
  );
}
